import { CopyButton } from '@/components/ui/copy-button';
import { cn } from '@/lib/cn';
import { formatElapsed } from '@/lib/formatElapsed';
import type { ChatMessage, FileUpload } from '@innermate/shared';
import { Pencil } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { AttachmentChips, statusToPhase } from './AttachmentChips';
import { Markdown } from './Markdown';
import {
  type StreamSegment,
  ToolCallChip,
  type ToolCallView,
  toolCallRecordToView,
} from './ToolCallChip';

interface Props {
  message: Pick<ChatMessage, 'role' | 'content'> & {
    attachedFileIds?: string[] | null;
    toolCalls?: ChatMessage['toolCalls'];
    responseTimeMs?: number | null;
    archivedAt?: string | null;
    metadata?: Record<string, unknown> | null;
  };
  /** 스트리밍 중 미저장 메시지 표시 (점멸 커서 등). */
  streaming?: boolean;
  /**
   * 스트리밍 중인 마지막 assistant bubble 이면 SSE 도착 순서대로 쌓인 세그먼트.
   * 이 prop 이 있으면 message.content / message.toolCalls 는 무시되고 세그먼트를 그대로 렌더.
   */
  streamSegments?: StreamSegment[];
  /** artifact tool chip 의 "패널 열기" 버튼 핸들러. */
  onArtifactClick?: (artifactId: string, version: number) => void;
  /** 답변 본문의 [[slug]] 위키링크 클릭 시 호출 — 우측 미리보기 패널 트리거. */
  onWikilinkPeek?: (slug: string) => void;
  /** [[slug]] chip 의 표시 텍스트를 페이지 제목으로 치환하기 위한 lookup. */
  slugToTitle?: Map<string, string>;
  /** 첨부 칩 status 룩업. 폴링되는 `['files', …]` 캐시에서 파생돼 ready/failed 로 reactive 갱신. */
  filesById?: Map<string, FileUpload>;
  /** 관리자가 응답 시간 표시를 켰는지. 켜진 경우에만 footer 노출. */
  showResponseTime?: boolean;
  /** 스트리밍 중인 placeholder 에 표시할 실시간 elapsed (ms). null/undefined 면 표시 안 함. */
  liveElapsedMs?: number | null;
  /**
   * 제공되면 (마지막 user 메시지) 복사 버튼 왼쪽에 편집 버튼 노출 + 인라인 편집 모드 활성화.
   * 편집 후 "보내기" 시 newContent 로 호출 — 호출자가 구 turn 삭제 + 재전송을 처리한다.
   */
  onEditResend?: (newContent: string) => void;
}

/** tool call → 사용자에게 보이는 짧은 한국어 활동 라벨. */
function toolCallToActivityLabel(call: ToolCallView): string {
  switch (call.toolKey) {
    case 'excel.list_my_files':
      return '엑셀 파일 찾는 중';
    case 'excel.describe':
      return '엑셀 파일 분석 중';
    case 'excel.head':
      return '엑셀 미리보는 중';
    case 'excel.query':
      return '엑셀 데이터 조회 중';
    case 'excel.save_query':
      return '쿼리 저장 중';
    case 'db.list_tables':
      return 'DB 테이블 살펴보는 중';
    case 'db.describe_table':
      return 'DB 테이블 분석 중';
    case 'db.run_query':
      return 'SQL 실행 중';
    case 'db.save_query':
      return '쿼리 저장 중';
    case 'web.fetch_url':
      return '웹 페이지 가져오는 중';
    case 'http.list_endpoints':
      return 'API 엔드포인트 확인 중';
    case 'http.request':
      return 'API 호출 중';
    case 'system.create_artifact': {
      const title = (call.input as { title?: string } | undefined)?.title;
      return title ? `${title} 생성 중` : '콘텐츠 생성 중';
    }
    case 'system.update_artifact': {
      const title = (call.input as { title?: string } | undefined)?.title;
      return title ? `${title} 수정 중` : '콘텐츠 수정 중';
    }
    default:
      return `${call.toolKey} 실행 중`;
  }
}

/** 마지막 segment 종류로 "현재 무엇을 하고 있는지" 추정. */
function streamActivityLabel(lastSeg: StreamSegment | undefined): string {
  if (!lastSeg) return '생각 중';
  if (lastSeg.type === 'text') return '응답 작성 중';
  if (lastSeg.type === 'status') return lastSeg.message;
  // toolcall
  if (lastSeg.call.status === 'pending') return toolCallToActivityLabel(lastSeg.call);
  return '다음 단계 준비 중';
}

function attachedFiles(
  ids: string[] | null | undefined,
  byId: Map<string, FileUpload> | undefined,
) {
  if (!ids || ids.length === 0) return [];
  return ids.map((id) => {
    const f = byId?.get(id);
    if (f) {
      return {
        id: f.id,
        originalName: f.originalName,
        sizeBytes: f.sizeBytes,
        kind: f.kind,
        phase: statusToPhase(f.status),
      };
    }
    return {
      id,
      originalName: '파일',
      sizeBytes: 0,
      kind: 'other' as FileUpload['kind'],
      phase: 'ready' as const,
      missing: true,
    };
  });
}

export function MessageBubble({
  message,
  streaming = false,
  streamSegments,
  onArtifactClick,
  onWikilinkPeek,
  slugToTitle,
  filesById,
  showResponseTime = false,
  liveElapsedMs,
  onEditResend,
}: Props) {
  const attached = attachedFiles(message.attachedFileIds, filesById);

  // compaction 으로 archived 된 메시지는 회색 + 가벼운 안내. UI 에 보존하되 LLM 컨텍스트엔 미포함.
  const isArchived = Boolean(message.archivedAt);

  // 마지막 user 메시지 인라인 편집 상태 (onEditResend 가 있을 때만 사용).
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const editRef = useRef<HTMLTextAreaElement>(null);

  const startEdit = () => {
    setDraft(message.content);
    setIsEditing(true);
  };
  const cancelEdit = () => setIsEditing(false);
  const submitEdit = () => {
    const trimmed = draft.trim();
    setIsEditing(false);
    if (trimmed.length === 0) return;
    onEditResend?.(trimmed);
  };

  // 편집 진입 시 포커스 + 커서 끝으로.
  useEffect(() => {
    if (!isEditing) return;
    const ta = editRef.current;
    if (!ta) return;
    ta.focus();
    ta.setSelectionRange(ta.value.length, ta.value.length);
  }, [isEditing]);

  // draft 변경마다 textarea 자동 높이 (composer 와 동일 패턴).
  // biome-ignore lint/correctness/useExhaustiveDependencies: draft 는 height 재계산 트리거 용도.
  useEffect(() => {
    if (!isEditing) return;
    const ta = editRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 240)}px`;
  }, [draft, isEditing]);

  // role='system' compaction summary row — 별도의 회색 박스로 명시.
  const meta = message.metadata as { kind?: string } | null | undefined;
  if (message.role === 'system' && meta?.kind === 'compaction') {
    return (
      <div className="rounded-lg border border-border bg-surface-muted/50 px-4 py-3 text-sm text-foreground-muted">
        <div className="text-xs font-medium text-foreground-faint mb-2">📦 이전 대화 요약</div>
        <div className="whitespace-pre-wrap break-words">{message.content}</div>
      </div>
    );
  }

  if (message.role === 'user') {
    if (isEditing) {
      return (
        <div className="flex flex-col items-end gap-2">
          {attached.length > 0 ? <AttachmentChips files={attached} size="sm" /> : null}
          <div className="w-full max-w-[80%] rounded-2xl bg-surface-muted px-4 py-2.5">
            <textarea
              ref={editRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  submitEdit();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  cancelEdit();
                }
              }}
              rows={1}
              className="w-full resize-none bg-transparent text-base outline-none"
            />
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={cancelEdit}
                className="rounded-md px-3 py-1 text-sm text-foreground-muted transition-colors hover:bg-surface hover:text-foreground"
              >
                취소
              </button>
              <button
                type="button"
                onClick={submitEdit}
                disabled={draft.trim().length === 0}
                className="rounded-md bg-accent px-3 py-1 text-sm text-accent-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
              >
                보내기
              </button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div
        className={`group flex flex-col items-end gap-1 ${isArchived ? 'opacity-50' : ''}`}
        title={isArchived ? '대화 압축으로 LLM 컨텍스트에서 제외됨 (화면에는 보존)' : undefined}
      >
        {attached.length > 0 ? <AttachmentChips files={attached} size="sm" /> : null}
        <div className="max-w-[80%] rounded-2xl bg-surface-muted px-4 py-2.5 text-base whitespace-pre-wrap break-words">
          {message.content}
        </div>
        <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
          {onEditResend ? (
            <button
              type="button"
              onClick={startEdit}
              aria-label="메시지 편집"
              title="메시지 편집"
              className="inline-flex size-5 items-center justify-center rounded-md text-foreground-muted transition-colors hover:bg-surface-muted/60 hover:text-foreground focus-visible:bg-surface-muted/60 focus-visible:text-foreground focus-visible:outline-none"
            >
              <Pencil className="size-3" />
            </button>
          ) : null}
          <CopyButton text={message.content.trimEnd()} label="메시지 복사" />
        </div>
      </div>
    );
  }
  if (message.role === 'assistant') {
    // 스트리밍 중인 placeholder bubble. 도착 순서 그대로 segment 를 렌더해 저장 후 모습과 일치.
    if (streamSegments) {
      const lastIdx = streamSegments.length - 1;
      const lastSeg = streamSegments[lastIdx];
      const activityLabel = streamActivityLabel(lastSeg);
      return (
        <div className="break-words space-y-2">
          {streamSegments.map((seg, i) => {
            if (seg.type === 'text') {
              return (
                // biome-ignore lint/suspicious/noArrayIndexKey: segments 는 append-only 이므로 index 가 안정적 id.
                <div key={`t-${i}`}>
                  <Markdown
                    content={seg.value}
                    onWikilinkPeek={onWikilinkPeek}
                    slugToTitle={slugToTitle}
                  />
                  {i === lastIdx ? (
                    <span
                      className="inline-block ml-1 w-1.5 h-4 align-middle bg-foreground-muted/70 animate-pulse"
                      aria-hidden
                    />
                  ) : null}
                </div>
              );
            }
            if (seg.type === 'toolcall') {
              return (
                <ToolCallChip key={seg.call.id} call={seg.call} onArtifactClick={onArtifactClick} />
              );
            }
            // status: 진행 상태 알림. 마지막 segment 면 pulse 점, 뒤에 다른 segment 가 있으면 정적.
            const isLast = i === lastIdx;
            return (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: streamSegments 는 append-only 이므로 index 가 안정적 id.
                key={`s-${i}`}
                className="text-sm text-foreground-muted bg-surface-muted border border-border rounded-md px-3 py-2 inline-flex items-center gap-2"
              >
                <span
                  className={cn(
                    'inline-block size-2 rounded-full bg-accent',
                    isLast && 'animate-pulse',
                  )}
                  aria-hidden
                />
                {seg.message}
              </div>
            );
          })}
          {/* 항상 마지막에 "지금 무엇을 하고 있는지" 라벨 + 점 3개. status 가 마지막이면 거기서 이미 같은 역할이라 생략. */}
          {lastSeg?.type !== 'status' ? (
            <div
              className="text-sm text-foreground-muted inline-flex items-center gap-1.5"
              aria-label={activityLabel}
            >
              <span>{activityLabel}</span>
              <span className="inline-flex items-center gap-0.5" aria-hidden>
                <span
                  className="inline-block size-1 rounded-full bg-foreground-muted/70 animate-pulse"
                  style={{ animationDelay: '0ms' }}
                />
                <span
                  className="inline-block size-1 rounded-full bg-foreground-muted/70 animate-pulse"
                  style={{ animationDelay: '200ms' }}
                />
                <span
                  className="inline-block size-1 rounded-full bg-foreground-muted/70 animate-pulse"
                  style={{ animationDelay: '400ms' }}
                />
              </span>
              {showResponseTime && liveElapsedMs != null ? (
                <span className="ml-1 tabular-nums text-foreground-faint">
                  {formatElapsed(liveElapsedMs)}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      );
    }

    // 저장된 assistant 메시지. 각 iteration 이 별개 row 로 내려오므로 JSX 순서 그대로가 시간순.
    const chips: ToolCallView[] | null =
      message.toolCalls && message.toolCalls.length > 0
        ? message.toolCalls.map(toolCallRecordToView)
        : null;
    const elapsed =
      showResponseTime && message.responseTimeMs != null && message.responseTimeMs >= 0
        ? formatElapsed(message.responseTimeMs)
        : null;
    const canceled =
      (message.metadata as { canceled?: boolean } | null | undefined)?.canceled === true;
    if (canceled) {
      return <div className="text-sm text-foreground-muted italic">사용자가 중단한 질문입니다</div>;
    }
    // 비정상 출력(무한 반복/끝없는 생성)으로 재생성을 반복했지만 끝내 실패한 placeholder.
    // sample(진단용 앞부분)은 metadata 에만 남기고 화면에는 노출하지 않는다.
    const runawayAborted =
      (message.metadata as { runawayAborted?: boolean } | null | undefined)?.runawayAborted ===
      true;
    if (runawayAborted) {
      return (
        <div className="text-sm text-foreground-muted italic">
          응답 생성에 반복적으로 실패했습니다. 잠시 후 다시 시도해 주세요.
        </div>
      );
    }
    // 본딩 내 모든 멤버가 비정상이거나(또는 단일 엔드포인트가 죽어) 쓸 수 있는 LLM 서버가 없던 turn.
    const noEndpoint =
      (message.metadata as { noEndpoint?: boolean } | null | undefined)?.noEndpoint === true;
    if (noEndpoint) {
      return (
        <div className="text-sm text-foreground-muted italic">
          현재 사용할 수 있는 LLM 서버가 없습니다. 잠시 후 다시 시도해 주세요.
        </div>
      );
    }
    return (
      <div className="break-words space-y-2">
        {message.content.length > 0 ? (
          <div>
            <Markdown
              content={message.content}
              onWikilinkPeek={onWikilinkPeek}
              slugToTitle={slugToTitle}
            />
            {streaming ? (
              <span
                className="inline-block ml-1 w-1.5 h-4 align-middle bg-foreground-muted/70 animate-pulse"
                aria-hidden
              />
            ) : null}
          </div>
        ) : null}
        {chips ? (
          <div className="flex flex-col gap-1.5">
            {chips.map((c) => (
              <ToolCallChip key={c.id} call={c} onArtifactClick={onArtifactClick} />
            ))}
          </div>
        ) : null}
        {elapsed ? (
          <div className="text-xs text-foreground-faint tabular-nums">{elapsed}</div>
        ) : null}
      </div>
    );
  }
  // system 은 디버그용 — 가볍게 표시. tool 메시지는 MessageList 에서 skip 하므로 여기 안 옴.
  return (
    <div
      className={cn(
        'rounded-md border border-border bg-surface px-3 py-2 text-xs text-foreground-muted font-mono',
      )}
    >
      [{message.role}] {message.content}
    </div>
  );
}
